import {
  enabledAtPath,
  isRowingBoatBookingEnabled,
  isRowingBoatMaintenanceEnabled,
  isRowingFleetEnabled,
  isRowingOutingsEnabled,
  isSailingBoatBookingEnabled,
  isSailingBoatMaintenanceEnabled,
  isSailingCrewFinderEnabled,
  isSailingOutingsEnabled,
  withModuleDefaults,
} from './moduleUtils';
import { getBespokeModuleSupport } from './teamMetadata';

export const ADMIN_PERMISSION_GROUPS = [
  {
    key: 'settings.manage',
    label: 'Club setup',
    description: 'Update the club profile, modules, access settings, branding, club URL, address, metadata, and public links.',
  },
  {
    key: 'members.manage',
    label: 'Member records',
    description: 'View and update members, tags, invites, imported members, emergency details, profile update requests, and qualifications including private evidence.',
  },
  {
    key: 'members.export',
    label: 'Download member data',
    description: 'Export selected member information as CSV files.',
  },
  {
    key: 'reports.manage',
    label: 'Reporting',
    description: 'Create, run, export, and manage shared reports and dashboards. Source-data permissions still apply.',
  },
  {
    key: 'membership.manage',
    label: 'Joining and membership',
    description: 'Manage the join form, pending members, waitlist, membership changes, membership plans/subscriptions, included additional accounts, child age limits, discounts, and bolt-ons.',
  },
  {
    key: 'finance.manage',
    label: 'Payments and finance',
    description: 'Manage Stripe, income dashboards, billing, payouts, refunds, accounting records, and Xero.',
  },
  {
    key: 'events.manage',
    label: 'Events and calendar',
    description: 'Create and edit events, locations, event roles, attendees, waitlists, volunteer requests, admin RSVP actions, and event registers.',
  },
  {
    key: 'courses.manage',
    label: 'Courses',
    description: 'Configure generic courses, participants, attendance, waitlists, reporting, and conversion.',
  },
  {
    key: 'bookingForms.manage',
    label: 'Booking forms',
    description: 'Create and publish versioned event and course booking forms and declarations.',
  },
  {
    key: 'bookingSensitiveAnswers.view',
    label: 'Sensitive booking answers',
    description: 'View and export emergency contacts in event registers and protected event and course answers. Every access is audited.',
  },
  {
    key: 'eventCheckIn.manage',
    label: 'Event check-in',
    description: 'Scan, check in, undo, void, and reissue paid event admission passes without editing events or managing refunds.',
  },
  {
    key: 'communications.manage',
    label: 'Communications',
    description: 'Manage message boards, moderation queues, individual and group emails, reusable email templates, and inbox broadcasts.',
  },
  {
    key: 'documents.manage',
    label: 'Documents',
    description: 'Create, update, remove, and upload club documents.',
  },
  {
    key: 'mentoring.manage',
    label: 'Mentoring',
    description: 'Review mentoring participants, manage matching, pairings, check-in status, and programme settings.',
  },
  {
    key: 'rideReports.manage',
    label: 'IAM RoadSmart ride reports',
    description: 'Configure ride report templates and settings, review reports, and manage the report archive.',
  },
  {
    key: 'rideDeclarations.manage',
    label: 'IAM RoadSmart ride declarations',
    description: 'Publish ride declarations, configure checkers, and review member signature and check history.',
  },
  {
    key: 'rowing.manage',
    label: 'Learn to Row',
    description: 'Manage Learn to Row courses, waitlists, participant records, and conversion settings.',
  },
  {
    key: 'rowingFleet.manage',
    label: 'Rowing fleet',
    description: 'Manage club-owned rowing boats and boat setup.',
  },
  {
    key: 'rowingBoatBooking.manage',
    label: 'Rowing boat booking',
    description: 'Manage rowing boat booking rules, member requests, booking overrides, and allocation.',
  },
  {
    key: 'rowingOutings.manage',
    label: 'Rowing outings',
    description: 'Manage rowing outing drafts, crews, boat preferences, submission, and reassignment.',
  },
  {
    key: 'rowingBoatMaintenance.manage',
    label: 'Rowing boat maintenance',
    description: 'Manage rowing boat maintenance tickets, updates, and out-of-service state.',
  },
  {
    key: 'sailing.manage',
    label: 'Sailing tools',
    description: 'Manage Learn to Sail courses, duty rosters, waitlists, and sailing admin tools.',
  },
  {
    key: 'moorings.manage',
    label: 'Moorings',
    description: 'Manage mooring maps, allocations, waiting lists, maintenance records, and linked mooring fees.',
  },
  {
    key: 'sailingFleet.manage',
    label: 'Sailing fleet',
    description: 'Manage club-owned sailing boats and mooring links.',
  },
  {
    key: 'sailingBoatBooking.manage',
    label: 'Sailing boat booking',
    description: 'Manage sailing boat booking rules, member requests, booking overrides, and allocation.',
  },
  {
    key: 'sailingOutings.manage',
    label: 'Club-led outings',
    description: 'Manage club-led sailing outing drafts, crews, boat preferences, submission, and reassignment.',
  },
  {
    key: 'sailingCrewFinder.manage',
    label: 'Crew Finder',
    description: 'Manage Crew Finder settings, posts, responses, and moderation.',
  },
  {
    key: 'sailingBoatMaintenance.manage',
    label: 'Sailing boat maintenance',
    description: 'Manage sailing boat maintenance tickets, updates, and out-of-service state.',
  },
];

export const ADMIN_PERMISSION_KEYS = ADMIN_PERMISSION_GROUPS.map((group) => group.key);

export function isAdminPermissionAvailableForModules(key, modules = {}, metadata = null) {
  const modulesWithDefaults = withModuleDefaults(modules);
  const bespokeSupport = metadata == null ? null : getBespokeModuleSupport(metadata);
  if (key === 'reports.manage') {
    return enabledAtPath(modulesWithDefaults, ['reporting']);
  }
  if ((key === 'rideReports.manage' || key === 'rideDeclarations.manage') && bespokeSupport?.iamRoadSmart === false) {
    return false;
  }
  if (key.startsWith('rowing') && bespokeSupport?.rowing === false) {
    return false;
  }
  if ((key.startsWith('sailing') || key === 'moorings.manage') && bespokeSupport?.sailing === false) {
    return false;
  }
  if (key === 'events.manage') {
    return enabledAtPath(modulesWithDefaults, ['events']);
  }
  if (key === 'courses.manage') {
    return enabledAtPath(modulesWithDefaults, ['courses']);
  }
  if (key === 'bookingForms.manage' || key === 'bookingSensitiveAnswers.view') {
    return enabledAtPath(modulesWithDefaults, ['events']) || enabledAtPath(modulesWithDefaults, ['courses']);
  }
  if (key === 'eventCheckIn.manage') {
    return enabledAtPath(modulesWithDefaults, ['events']);
  }
  if (key === 'communications.manage') {
    return [
      ['communications', 'messages'],
      ['communications', 'email'],
      ['communications', 'inboxBroadcasts'],
      ['communications', 'boards'],
    ].some((path) => enabledAtPath(modulesWithDefaults, path));
  }
  if (key === 'documents.manage') {
    return enabledAtPath(modulesWithDefaults, ['documents']);
  }
  if (key === 'mentoring.manage') {
    return enabledAtPath(modulesWithDefaults, ['mentoring']);
  }
  if (key === 'rideReports.manage') {
    return enabledAtPath(modulesWithDefaults, ['iamRoadSmart', 'rideReports']);
  }
  if (key === 'rideDeclarations.manage') {
    return enabledAtPath(modulesWithDefaults, ['iamRoadSmart', 'rideDeclarations']);
  }
  if (key === 'rowing.manage') {
    return enabledAtPath(modulesWithDefaults, ['rowing', 'learnToRow']);
  }
  if (key === 'rowingFleet.manage') {
    return isRowingFleetEnabled(modulesWithDefaults);
  }
  if (key === 'rowingBoatBooking.manage') {
    return isRowingBoatBookingEnabled(modulesWithDefaults);
  }
  if (key === 'rowingOutings.manage') {
    return isRowingOutingsEnabled(modulesWithDefaults);
  }
  if (key === 'rowingBoatMaintenance.manage') {
    return isRowingBoatMaintenanceEnabled(modulesWithDefaults);
  }
  if (key === 'sailing.manage') {
    return [
      ['sailing', 'learnToSail'],
      ['sailing', 'dutyRoster'],
    ].some((path) => enabledAtPath(modulesWithDefaults, path));
  }
  if (key === 'moorings.manage') {
    return enabledAtPath(modulesWithDefaults, ['sailing', 'moorings']);
  }
  if (key === 'sailingFleet.manage') {
    return enabledAtPath(modulesWithDefaults, ['sailing', 'boats']);
  }
  if (key === 'sailingBoatBooking.manage') {
    return isSailingBoatBookingEnabled(modulesWithDefaults);
  }
  if (key === 'sailingOutings.manage') {
    return isSailingOutingsEnabled(modulesWithDefaults);
  }
  if (key === 'sailingCrewFinder.manage') {
    return isSailingCrewFinderEnabled(modulesWithDefaults);
  }
  if (key === 'sailingBoatMaintenance.manage') {
    return isSailingBoatMaintenanceEnabled(modulesWithDefaults);
  }
  return true;
}

export function getAdminPermissionGroupsForModules(modules = {}, { includeKeys = [], metadata = null } = {}) {
  const included = new Set(normalizePermissionKeys(includeKeys));
  return ADMIN_PERMISSION_GROUPS
    .map((group) => ({
      ...group,
      available: isAdminPermissionAvailableForModules(group.key, modules, metadata),
    }))
    .filter((group) => group.available || included.has(group.key));
}

export function normalizePermissionKeys(keys) {
  if (!Array.isArray(keys)) return [];
  const allowed = new Set(ADMIN_PERMISSION_KEYS);
  return Array.from(new Set(
    keys
      .map((key) => (typeof key === 'string' ? key.trim() : ''))
      .filter((key) => allowed.has(key))
  )).sort((a, b) => a.localeCompare(b));
}

function expandLegacyPermissions(keys = [], assignment = null) {
  const next = new Set(keys);
  const schemaVersion = Number(assignment?.permissionSchemaVersion || 0);
  if (schemaVersion < 2 && next.has('sailingFleet.manage')) {
    next.add('sailingBoatBooking.manage');
    next.add('sailingOutings.manage');
  }
  if (schemaVersion < 3 && next.has('rowing.manage')) {
    next.add('rowingFleet.manage');
    next.add('rowingBoatBooking.manage');
    next.add('rowingOutings.manage');
    next.add('rowingBoatMaintenance.manage');
  }
  if (schemaVersion < 3 && next.has('sailingFleet.manage')) {
    next.add('sailingBoatMaintenance.manage');
  }
  return Array.from(next).sort((a, b) => a.localeCompare(b));
}

export function buildPermissionState({ isFullAdmin = false, assignment = null } = {}) {
  const keys = isFullAdmin
    ? ADMIN_PERMISSION_KEYS
    : expandLegacyPermissions(normalizePermissionKeys(assignment?.permissionKeys), assignment);
  const keySet = new Set(keys);
  return {
    isFullAdmin: Boolean(isFullAdmin),
    isLimitedAdmin: !isFullAdmin && keys.length > 0,
    keys,
    roleId: assignment?.roleId || '',
    roleName: assignment?.roleName || '',
    has: (key) => Boolean(isFullAdmin || keySet.has(key)),
    hasAny: (...keysToCheck) => Boolean(isFullAdmin || keysToCheck.flat().some((key) => keySet.has(key))),
  };
}

export function getPermissionLabel(key) {
  return ADMIN_PERMISSION_GROUPS.find((group) => group.key === key)?.label || key;
}
